import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import { Plus, Pencil, Trash2 } from "lucide-react";
import MoneyBurst from "@/components/MoneyBurst";
import DateField from "@/components/DateField";

const FREQS = ["monthly","one-time","weekly","bi-weekly","quarterly","yearly"];

export default function Income() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ source: "", amount: "", frequency: "monthly", date: new Date().toISOString().slice(0,10), notes: "" });
  const [burst, setBurst] = useState(0); // increments to fire a MoneyBurst

  const load = useCallback(async () => {
    const { data } = await api.get("/income");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEdit(null); setForm({ source:"", amount:"", frequency:"monthly", date:new Date().toISOString().slice(0,10), notes:"" }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ source:i.source, amount:i.amount, frequency:i.frequency, date:i.date, notes:i.notes || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (edit) await api.put(`/income/${edit.id}`, payload);
      else await api.post("/income", payload);
      toast.success(edit ? "Income updated" : "Income added");
      setBurst(b => b + 1); // fire particle burst on the save button
      setTimeout(() => { setOpen(false); load(); }, 350); // let the burst be visible briefly
    } catch { toast.error(t("common.toast_failed")); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this income entry?")) return;
    try { await api.delete(`/income/${id}`); toast.success(t("common.toast_deleted")); load(); } catch { toast.error(t("common.toast_failed")); }
  };

  const cur = user?.currency || "USD";
  const total = items.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div>
      <PageHeader
        subtitle={t("pages.income.subtitle")}
        title={t("pages.income.title")}
        action={<button className="btn-primary" onClick={startNew} data-testid="add-income-btn"><Plus size={16}/> {t("pages.income.add")}</button>}
      />

      <div className="grid md:grid-cols-3 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.income.summary_total_sources", { defaultValue: "Total Sources" })} value={items.length}/>
        <SummaryStat label={t("pages.income.summary_sum", { defaultValue: "Sum of Entries" })} value={formatMoney(total, cur)}/>
        <SummaryStat label={t("common.currency")} value={cur}/>
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.income.empty_title")} body={t("pages.income.empty_hint")}
          action={<button className="btn-primary" onClick={startNew} data-testid="empty-add-income-btn"><Plus size={16}/> {t("pages.income.add")}</button>} />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
              <tr>
                <Th>{t("common.source")}</Th><Th>{t("common.frequency")}</Th><Th>{t("common.date")}</Th><Th className="text-right">{t("common.amount")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className="border-t border-[var(--border-default)] hover:bg-[var(--bg-secondary)]/60">
                  <Td>
                    <div className="font-medium">{i.source}</div>
                    {i.notes && <div className="text-xs text-[var(--text-muted)]">{i.notes}</div>}
                  </Td>
                  <Td><span className="badge badge-neutral">{t(`common.${i.frequency.replace("-","")}`, { defaultValue: i.frequency })}</span></Td>
                  <Td className="text-[var(--text-secondary)]">{i.date}</Td>
                  <Td className="text-right num-md">{formatMoney(i.amount, cur)}</Td>
                  <Td className="text-right">
                    <button className="btn-danger-ghost" onClick={() => startEdit(i)} data-testid={`edit-income-${i.id}`}><Pencil size={15}/></button>
                    <button className="btn-danger-ghost" onClick={() => remove(i.id)} data-testid={`del-income-${i.id}`}><Trash2 size={15}/></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? `${t("common.edit")} · ${t("pages.income.subtitle")}` : t("pages.income.add")}>
        <form onSubmit={save} className="grid gap-4">
          <Field label={t("common.source")}><input className="field-input" required value={form.source} onChange={e => setForm({...form, source: e.target.value})} placeholder="Salary, Freelance…" data-testid="input-income-source"/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={`${t("common.amount")} (${cur})`}><input type="number" step="0.01" className="field-input" required value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="input-income-amount"/></Field>
            <Field label={t("common.frequency")}>
              <select className="field-input" value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})} data-testid="input-income-frequency">
                {FREQS.map(f => <option key={f} value={f}>{t(`common.${f.replace("-","")}`, { defaultValue: f })}</option>)}
              </select>
            </Field>
          </div>
          <Field label={t("common.date")}><DateField value={form.date} onChange={v => setForm({...form, date: v})} testid="input-income-date"/></Field>
          <Field label={`${t("common.notes")} (${t("common.optional")})`}><input className="field-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="input-income-notes"/></Field>
          <div className="flex justify-end gap-2 relative">
            <MoneyBurst trigger={burst} tone="pos" />
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-income-btn">{edit ? t("common.save_changes") : t("pages.income.add")}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
export function Th({ children, className = "" }) { return <th className={`text-left font-semibold text-[11px] tracking-[0.16em] uppercase px-5 py-3 ${className}`}>{children}</th>; }
export function Td({ children, className = "" }) { return <td className={`px-5 py-3 ${className}`}>{children}</td>; }
export function SummaryStat({ label, value }) {
  return (
    <div className="card-surface p-5">
      <div className="label-eyebrow">{label}</div>
      <div className="num-lg mt-2">{value}</div>
    </div>
  );
}
